import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9u56nd3pcjqs2fcsmbg';

const aboutJsonLd = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  "name": "关于我们 - kaiyun体育",
  "description": "kaiyun体育是专业的在线体育平台，提供全球体育赛事资讯与直播服务。了解我们的品牌故事、发展历程与口碑荣誉。",
  "url": "https://yunmatch.github.io/about",
  "isPartOf": {
    "@type": "WebSite",
    "name": "kaiyun体育",
    "url": "https://yunmatch.github.io/"
  }
};

const breadcrumbJsonLd = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://yunmatch.github.io/" },
    { "@type": "ListItem", "position": 2, "name": "关于我们" }
  ]
};

const milestones = [
  { year: '2018', title: '品牌创立', desc: 'kaiyun体育平台正式上线，以足球赛事资讯为起点，开启体育内容服务之路。' },
  { year: '2019', title: '品类拓展', desc: '新增篮球、网球两大核心品类，覆盖全球主流体育联赛，用户突破百万。' },
  { year: '2020', title: '技术升级', desc: '推出实时数据同步系统，实现赛事比分秒级更新，直播体验全面优化。' },
  { year: '2021', title: '全球布局', desc: '业务覆盖全球180+国家和地区，新增电竞、赛车等热门品类。' },
  { year: '2022', title: '生态构建', desc: '建立专业分析师团队，推出深度赛事解读和数据分析服务。' },
  { year: '2023', title: '行业领先', desc: '累计用户突破5000万，成为中文互联网领先的体育资讯平台。' },
  { year: '2024', title: '持续创新', desc: 'AI驱动的个性化内容推荐上线，为用户带来更智能的体育资讯体验。' },
];

interface FormState {
  name: string;
  email: string;
  subject: string;
  message: string;
}

export default function AboutPage() {
  const [activeMilestone, setActiveMilestone] = useState(0);
  const [formState, setFormState] = useState<FormState>({ name: '', email: '', subject: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [charCount, setCharCount] = useState(0);

  const handleChange = (field: keyof FormState, value: string) => {
    if (field === 'message' && value.length > 500) return;
    setFormState((prev) => ({ ...prev, [field]: value }));
    if (field === 'message') setCharCount(value.length);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    setFormError('');

    try {
      const form = e.currentTarget;
      const honeypot = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
      if (honeypot && honeypot.value.trim() !== '') {
        setFormStatus('success');
        setFormState({ name: '', email: '', subject: '', message: '' });
        setCharCount(0);
        setTimeout(() => setFormStatus('idle'), 3000);
        return;
      }

      const formData = new FormData(form);
      if (honeypot) formData.delete('website_alt');

      const submitAddr = FORM_SUBMIT_URL;
      const response = await fetch(submitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormState({ name: '', email: '', subject: '', message: '' });
        setCharCount(0);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <title>关于我们 - kaiyun体育 | 全球体育赛事资讯平台</title>
      <meta name="description" content="kaiyun体育是专业的在线体育平台，提供全球体育赛事资讯与直播服务。了解我们的品牌故事、发展历程、口碑荣誉与联系方式。" />
      <meta name="keywords" content="kaiyun体育,关于kaiyun体育,kaiyun体育平台介绍,体育平台,联系方式" />
      <link rel="canonical" href="https://yunmatch.github.io/about" />
      <meta property="og:title" content="关于我们 - kaiyun体育 | 全球体育赛事资讯平台" />
      <meta property="og:description" content="kaiyun体育是专业的在线体育平台，提供全球体育赛事资讯与直播服务。" />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://yunmatch.github.io/about" />
      <script type="application/ld+json">{JSON.stringify(aboutJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>

      <Navbar />

      <main>
        <div className="pt-20 pb-6 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
            <div className="py-8 md:py-10">
              <h1 className="font-heading text-2xl md:text-5xl font-bold text-foreground-950 mb-3">
                关于 kaiyun体育
              </h1>
              <p className="text-foreground-600 text-sm md:text-lg max-w-2xl">
                专业的在线体育平台，致力于为全球体育爱好者提供最全面、最及时、最专业的体育资讯与赛事直播服务
              </p>
            </div>
          </div>
        </div>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  品牌介绍
                </h2>
                <div className="space-y-4 text-foreground-700 text-sm md:text-base leading-relaxed">
                  <p>
                    <strong className="text-foreground-950">kaiyun体育</strong>成立于2018年，是一家专注于全球体育赛事资讯与直播服务的综合性平台。自创立以来，我们始终秉持"让体育更精彩"的理念，为全球用户提供高质量、全方位的体育内容服务。
                  </p>
                  <p>
                    平台目前覆盖足球、篮球、网球、电竞、赛车、搏击、高尔夫等30余个体育项目，日均更新赛事资讯超过500篇，实时追踪全球100+顶级联赛和赛事。我们的专业分析师团队由资深体育记者和数据专家组成，为用户提供深度的赛事解读和专业的数据分析。
                  </p>
                  <p>
                    凭借卓越的产品体验和优质的内容服务，<strong className="text-foreground-950">kaiyun体育</strong>已经成长为中文互联网领先的体育资讯平台，累计服务超过5000万用户，覆盖全球180多个国家和地区。
                  </p>
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20sports%20media%20office%20with%20multiple%20screens%20showing%20sports%20data%2C%20professional%20workspace%20with%20warm%20lighting%2C%20clean%20and%20modern%20interior%2C%20editorial%20photography%20style%2C%20sports%20analysis%20environment%2C%20neutral%20warm%20tones%2C%20professional%20atmosphere&width=1200&height=800&seq=kaiyun-about-brand&orientation=landscape"
                  alt="kaiyun体育品牌"
                  className="w-full h-72 md:h-80 object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-12">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">
                发展历程
              </h2>
              <p className="text-foreground-600 text-sm md:text-base">
                从起步到领先，每一步都坚实有力
              </p>
            </div>

            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300 -translate-x-1/2 hidden md:block"></div>

              <div className="space-y-6 md:space-y-0">
                {milestones.map((m, index) => (
                  <div
                    key={m.year}
                    className={`relative flex flex-col md:flex-row gap-4 md:gap-8 items-start md:items-center ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'} hidden md:block`}>
                      <div
                        onClick={() => setActiveMilestone(index)}
                        className={`inline-block cursor-pointer transition-all duration-200 ${
                          activeMilestone === index ? 'scale-105' : 'opacity-60 hover:opacity-80'
                        }`}
                      >
                        <h3 className="font-heading text-lg font-bold text-foreground-950">{m.title}</h3>
                        <p className="text-xs text-foreground-500 mt-1">{m.desc}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 md:justify-center md:flex-shrink-0">
                      <div
                        onClick={() => setActiveMilestone(index)}
                        className={`w-12 h-12 md:w-10 md:h-10 rounded-full flex items-center justify-center font-heading font-bold text-sm cursor-pointer transition-all duration-200 ${
                          activeMilestone === index
                            ? 'bg-primary-500 text-background-50 scale-110'
                            : 'bg-background-200 text-foreground-600'
                        }`}
                      >
                        {m.year.slice(2)}
                      </div>

                      <div className="md:hidden">
                        <h3 className="font-heading text-base font-bold text-foreground-950">{m.title}</h3>
                        <p className="text-xs text-foreground-500 mt-0.5">{m.desc}</p>
                      </div>
                    </div>

                    <div className={`flex-1 ${index % 2 === 0 ? 'md:text-left' : 'md:text-right'} hidden md:block`}></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-12">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">
                口碑荣誉
              </h2>
              <p className="text-foreground-600 text-sm md:text-base">
                用户的认可，是我们前进的最大动力
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {[
                { icon: 'ri-award-line', title: '最佳体育资讯平台', year: '2023', org: '中国互联网协会' },
                { icon: 'ri-star-line', title: '年度创新产品奖', year: '2023', org: '体育产业峰会' },
                { icon: 'ri-shield-check-line', title: '用户最信赖品牌', year: '2024', org: '消费者评测机构' },
                { icon: 'ri-thumb-up-line', title: '最佳用户体验奖', year: '2024', org: '数字产品评测' },
              ].map((award) => (
                <div key={award.title} className="bg-background-100 rounded-xl p-5 md:p-6 border border-background-200/60 text-center">
                  <div className="w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-3">
                    <i className={`${award.icon} text-xl text-accent-700`}></i>
                  </div>
                  <h4 className="font-heading font-semibold text-foreground-950 text-sm mb-1">{award.title}</h4>
                  <p className="text-xs text-foreground-500">{award.year} · {award.org}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100" id="contact">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  联系我们
                </h2>
                <p className="text-foreground-600 text-sm md:text-base mb-6">
                  如有任何问题或合作意向，欢迎通过以下方式联系我们
                </p>

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">客服热线</p>
                      <p className="text-sm text-foreground-600">400-888-6666</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-accent-700"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">电子邮箱</p>
                      <p className="text-sm text-foreground-600">contact@kaiyun-sports.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-secondary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">公司地址</p>
                      <p className="text-sm text-foreground-600">北京市朝阳区建国路88号SOHO现代城A座</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <form
                  id="contact-form"
                  data-readdy-form="true"
                  onSubmit={handleSubmit}
                  className="bg-background-100 rounded-xl p-5 md:p-6 border border-background-200/60"
                >
                  <h3 className="font-heading font-semibold text-foreground-950 mb-4">发送消息</h3>

                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="form-anti-spam"
                  />

                  <div className="space-y-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        姓名
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        value={formState.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        邮箱
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        value={formState.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-subject" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        主题
                      </label>
                      <input
                        id="contact-subject"
                        name="subject"
                        type="text"
                        required
                        value={formState.subject}
                        onChange={(e) => handleChange('subject', e.target.value)}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入消息主题"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        留言内容
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        rows={4}
                        maxLength={500}
                        value={formState.message}
                        onChange={(e) => handleChange('message', e.target.value)}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言（限500字）"
                      ></textarea>
                      <p className="text-xs text-foreground-500 mt-1 text-right">{charCount}/500</p>
                    </div>
                  </div>

                  {formError && (
                    <div className="mt-4 p-3 rounded-lg bg-primary-50 text-primary-700 text-sm">
                      {formError}
                    </div>
                  )}

                  {formStatus === 'success' && (
                    <div className="mt-4 p-3 rounded-lg bg-accent-50 text-accent-700 text-sm">
                      消息已发送成功，我们会尽快回复您！
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full mt-4 px-6 py-3 bg-primary-500 text-background-50 font-medium rounded-lg hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap transition-all text-sm"
                  >
                    {formStatus === 'submitting' ? '发送中...' : '发送消息'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}